import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useGetExistingEventStationeryDraftsWithTemplateQuery } from './ExistingDraftsWarningProvider.graphql.generated';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { createContext } from '@shared/utils/createContext';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useDisclosure } from '@withjoy/joykit';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import type { MinimalStationeryTemplate } from './types';
import { ExistingDraftsWarningDialog } from './ExistingDraftsWarningDialog';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventUserRole } from '@shared/components/AuthProvider';

type ExistingDraftsWarningContext = {
  /**
   * If there are existing drafts for the given template, show a warning dialog.
   * Else, call the createNewDraftCallback.
   */
  checkForExistingDraftOrCreateNew: (
    templateId: string,
    createNewDraftCallback: (existingDraftsCount: number) => void,
    onShowWarningDialog: () => void,
    isDigital?: boolean,
    /**
     * Called when the user dismisses the dialog without choosing either action. Most callers can
     * ignore it: they sit on a page that is still there behind the dialog. A caller that *replaced*
     * its page to get here — the envelope entry route, which renders only a loader — must know, or
     * dismissing leaves the user on that loader with nothing to do.
     */
    onDismiss?: () => void,
  ) => void;
  /**
   * Whether the drafts lookup is still in flight. Until it settles the map is empty and
   * `checkForExistingDraftOrCreateNew` reports "no existing drafts" for everything — fine behind a
   * human click, wrong for a caller that fires on mount.
   */
  isLoading: boolean;
};

const [Provider, useExistingDraftsWarningContext] = createContext<ExistingDraftsWarningContext>({ name: 'ExistingDraftsWarning' });

type StationeryTemplateMap = {
  [templateId_format: string]: {
    template: MinimalStationeryTemplate;
    matchingDraftIds: string[];
  };
};

const getTemplateConfigMapKey = (draft: { format: StationeryDraftFormat; stationeryTemplate: { id: string } }) => {
  return `${draft.stationeryTemplate.id}_${draft.format}`;
};

/**
 * Returns a map of templateId to config object containing the template and matching draft ids
 */
const useTemplateConfigMap = (eventId: string | undefined) => {
  const { isAdmin, fetching } = useEventUserRole();
  const isAdminStale = !isAdmin || fetching;
  const { data, loading } = useGetExistingEventStationeryDraftsWithTemplateQuery({
    variables: {
      input: {
        eventId: eventId!,
      },
    },
    skip: isAdminStale || !eventId,
    fetchPolicy: 'cache-and-network',
    batchMode: 'fast',
  });

  const templateToConfigMap = useMemo(() => {
    return (
      data?.stationeryDraftsByEvent.reduce((acc, draft) => {
        const key = getTemplateConfigMapKey(draft);

        // Get or create the config object
        const config = acc[key] ?? {};
        config.template = draft.stationeryTemplate;

        // Get or create the matchingDraftIds array
        const matchingDraftIds = config.matchingDraftIds || [];
        matchingDraftIds.push(draft.id);
        config.matchingDraftIds = matchingDraftIds;

        // Store the config object in the map
        acc[key] = config;

        return acc;
      }, {} as StationeryTemplateMap) || {}
    );
  }, [data]);

  // `fetching` covers the window before the role resolves, when the query has not fired yet and an
  // empty map is not yet an answer. A settled non-admin never fires it at all, so that is not
  // loading. `cache-and-network` keeps `loading` true through the background refresh, so pair it
  // with `!data` — a cached map is answer enough.
  const isLoading = !!eventId && !data && (fetching || loading);

  return { templateToConfigMap, isLoading };
};

const ExistingDraftsWarningProvider: React.FC<{ eventId: string | undefined }> = ({ children, eventId }) => {
  const { buttonInteracted } = useCardTelemetry();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Store the createNewDraftCallback in a ref so that it can be called when the user
  // confirms they want to create a new draft in the warning dialog
  const createNewDraftCallbackRef = useRef<((existingDraftsCount: number) => void) | null>(null);

  // Cleared as soon as it fires, and whenever a choice is made instead, so a later dismissal of an
  // unrelated dialog cannot replay the previous caller's handler.
  const onDismissRef = useRef<(() => void) | null>(null);

  const { templateToConfigMap, isLoading } = useTemplateConfigMap(eventId);
  const { goToCustomizeDraft, goToCardDrafts, goToDigitalCustomizeDraft } = useCardsRouterContext();

  // Store the current template config map key to know which variant dialog to show
  const [currentTemplateConfigKey, setCurrentTemplateConfigKey] = useState<string | null>(null);

  const [isDigital, setIsDigital] = useState<boolean>(false);

  const checkForExistingDraftOrCreateNew = useCallback<ExistingDraftsWarningContext['checkForExistingDraftOrCreateNew']>(
    (templateId, createNewDraftCallback, onShowWarningDialog, isDigital, onDismiss) => {
      const key = getTemplateConfigMapKey({ stationeryTemplate: { id: templateId }, format: isDigital ? StationeryDraftFormat.digital : StationeryDraftFormat.print });
      const config = templateToConfigMap[key];

      // If there are existing drafts, show the dialog
      if (config && config.matchingDraftIds.length > 0) {
        // Store the createNewDraftCallback in a ref so that it can be called after the dialog is closed
        // and the user confirms they want to create a new draft
        createNewDraftCallbackRef.current = createNewDraftCallback;
        onDismissRef.current = onDismiss ?? null;
        setCurrentTemplateConfigKey(key);
        setIsDigital(!!isDigital);
        onShowWarningDialog();
        onOpen();
        return;
      }

      // If there are no existing drafts, create a new draft
      createNewDraftCallback(0);
    },
    [templateToConfigMap, onOpen],
  );

  const templateConfig = currentTemplateConfigKey ? templateToConfigMap[currentTemplateConfigKey] : null;

  // Only the dialog's own close affordance routes here. The action handlers below call `onClose`
  // directly, so choosing an action never reads as a dismissal.
  const handleOnDismiss = useEventCallback(() => {
    onClose();
    const onDismiss = onDismissRef.current;
    onDismissRef.current = null;
    onDismiss?.();
  });

  const handleOnViewOrEditExistingDraftClick = useEventCallback(() => {
    onDismissRef.current = null;
    if (templateConfig) {
      const draftIds = templateConfig.matchingDraftIds;
      // If there are multiple drafts, go to the drafts page
      if (draftIds.length > 1) {
        onClose();
        // Wait for the dialog to close before navigating
        setTimeout(() => {
          buttonInteracted({
            name: 'existingDraftsWarningDialog_viewAll',
            themeId: templateConfig.template.themeId,
            existingTemplateDraftsCount: draftIds.length,
          });
          goToCardDrafts();
        }, 500);
      } else {
        buttonInteracted({
          name: 'existingDraftsWarningDialog_editExisting',
          themeId: templateConfig.template.themeId,
          existingTemplateDraftsCount: draftIds.length,
        });
        // If there is only one draft, go to the customize draft page
        if (isDigital) {
          // TODO: check if digital drafts exist instead of all kind of drafts matching the template. (backend required)
          goToDigitalCustomizeDraft(draftIds[0]);
        } else {
          goToCustomizeDraft(draftIds[0]);
        }
      }
    }
  });
  const handleOnCreateNewDraftClick = useEventCallback(() => {
    onDismissRef.current = null;
    // Call the createNewDraftCallback that we collected when we determined there 1 or more drafts
    // and that the user should see the dialog
    createNewDraftCallbackRef.current?.(templateConfig?.matchingDraftIds.length ?? 0);
    if (templateConfig) {
      buttonInteracted({
        name: 'existingDraftsWarningDialog_createNewDraft',
        themeId: templateConfig.template.themeId,
        stationeryTemplateCategory: templateConfig.template.category,
        existingTemplateDraftsCount: templateConfig.matchingDraftIds.length,
      });
    }
  });

  return (
    <Provider value={{ checkForExistingDraftOrCreateNew, isLoading }}>
      {children}

      {/* Only show the dialog if there is a template config */}
      {templateConfig && (
        <ExistingDraftsWarningDialog
          isOpen={isOpen}
          handleOnClose={handleOnDismiss}
          template={templateConfig.template}
          handleOnCreateNewDraftClick={handleOnCreateNewDraftClick}
          handleOnViewOrEditExistingDraftClick={handleOnViewOrEditExistingDraftClick}
          variant={templateConfig.matchingDraftIds.length > 1 ? 'multiple' : 'single'}
        />
      )}
    </Provider>
  );
};

export { ExistingDraftsWarningProvider, useExistingDraftsWarningContext };
